import "./account.scss";

import { Component } from "react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import classnames from "classnames";

import BlockingModal from "common/blocking_modal";
import ModalHeader from "common/blocking_modal/header";
import ModalSubheader from "common/blocking_modal/subheader";
import ModalGraphic from "common/blocking_modal/graphic";
import ModalActions from "common/blocking_modal/actions";
import Button from "common/core/button";
import destructiveAction from "assets/images/destructive-action.svg";
import GoogleLogo from "assets/images/account/google-logo.svg";
import Env from "config/environment";

import Styles from "./google_signin_button.module.scss";

const GAPI_NOT_LOADED = "GAPI_NOT_LOADED";
const IDPIFRAME_INITIALIZATION_FAILED = "idpiframe_initialization_failed";
const { googleSignInAppHost } = Env;

export default class GoogleSignInButton extends Component {
  state = {
    isBlocked: false,
    hidden: true,
  };

  componentDidMount() {
    // if ghostery prevents google API from loading, the gapi object is still present
    // check for presence of load function instead
    if (!this.auth2 && window.gapi?.load) {
      // Create a new Auth2 Object if this component is being rendered
      // after the initial "google-loaded" event has been fired.
      this.googleInit()
        .then(() => {
          this.setState({ hidden: false });
        })
        .catch((err) => {
          // This mostly occurs now due to 3rd party cookies being disabled.
          // Since Google login will not work in that case, hide the button.
          if (err && err.error === IDPIFRAME_INITIALIZATION_FAILED) {
            this.setState({ hidden: true });
          }
        });
    }
  }

  closeModal() {
    this.setState({ isBlocked: false });
  }

  authorize() {
    const { onWillSignIn, canSignIn } = this.props;
    onWillSignIn();
    if (canSignIn) {
      this.googleInit()
        .then(() => this.requestAuthCode())
        // Google's library has a tendency toward throwing exceptions for basic
        // behaviours such as closing the authentication popup. We ignore these.
        .catch((err) => {
          if (err && err.type === GAPI_NOT_LOADED) {
            this.setState({ isBlocked: true });
          }
        });
    }
  }

  requestAuthCode() {
    return this.auth2
      .grantOfflineAccess({ redirect_uri: "postmessage" })
      .then((authCode) => this.props.signIn(authCode));
  }

  googleInit() {
    return new Promise((resolve, reject) => {
      if (this.auth2) {
        resolve();
      } else if (!window.gapi?.load) {
        reject({ type: GAPI_NOT_LOADED });
      } else {
        window.gapi.load("auth2", () => {
          this.auth2 = window.gapi.auth2.getAuthInstance();

          if (!this.auth2) {
            // If an Google Auth2 object doesn't already exist, initialize one.
            this.auth2 = window.gapi.auth2.init({
              client_id: googleSignInAppHost,
              scope: "profile email",
            });
          }

          this.auth2.then(resolve, reject);
        });
      }
    });
  }

  render() {
    const { className, forSignUp } = this.props;
    const { hidden, isBlocked } = this.state;
    if (hidden) {
      return null;
    }

    const classNames = classnames(Styles.googleBtn, className);

    return (
      <>
        <button
          id="google-signin-button"
          type="button"
          className={classNames}
          onClick={this.authorize.bind(this)}
          data-automation-id="google-sign-in-button"
        >
          <img src={GoogleLogo} alt="" aria-hidden="true" />
          {forSignUp ? (
            <FormattedMessage
              id="959d1d6c-7528-4e70-882e-2fc9642c5023"
              defaultMessage="Sign up with Google"
            />
          ) : (
            <FormattedMessage
              id="9c94a830-16ff-40e3-b914-3b67cfd2c5c8"
              defaultMessage="Sign in with Google"
            />
          )}
        </button>
        <BlockingModal isOpen={isBlocked}>
          <ModalGraphic>
            <img alt="destroy" src={destructiveAction} />
          </ModalGraphic>
          <ModalHeader>
            <FormattedMessage
              id="85689bdf-66ac-419e-ad4a-379494faee03"
              description="header"
              defaultMessage="Are you using an ad blocker?"
            />
          </ModalHeader>
          <ModalSubheader>
            <FormattedMessage
              id="df36f0be-09d0-4824-b741-11ee019fda5d"
              description="subheader"
              defaultMessage="It looks like an ad blocker is preventing Google+ sign-in. Please disable it or use your email address to continue." // eslint-disable-line
            />
          </ModalSubheader>
          <ModalActions>
            <Button
              onClick={this.closeModal.bind(this)}
              buttonColor="action"
              buttonSize="large"
              variant="secondary"
            >
              <FormattedMessage
                id="fdf43b99-143c-4fad-8957-b9e9d8607a11"
                description="useEmail"
                defaultMessage="Use email instead"
              />
            </Button>
          </ModalActions>
        </BlockingModal>
      </>
    );
  }
}

GoogleSignInButton.propTypes = {
  canSignIn: PropTypes.bool,
  onWillSignIn: PropTypes.func,
  signIn: PropTypes.func.isRequired,
  forSignUp: PropTypes.bool,
  className: PropTypes.string,
};

GoogleSignInButton.defaultProps = {
  canSignIn: true,
  onWillSignIn: () => {},
  className: null,
};
